import { createFileRoute } from '@tanstack/react-router'
import React, { useEffect, useState } from 'react'
import WP2SlideCover from '~/client/components/present/WP2Template/WP2SlideCover'
import MTSlideCover from '~/client/components/present/MTTemplate/MTSlideCover'
import FreshfieldsSlideCover from '~/client/components/present/FreshfieldsTemplate/FreshfieldsSlideCover'
import { EventSpaceList } from '~/shared/event/event.types'
import NewDefaultSlideCover from '~/client/components/present/DefaultTemplate/NewDefaultSlideCover'

type TemplateKey = 'warburg' | 'wp2' | 'mt' | 'default' | 'freshfields'

const makeEventFromParams = (name: string, location?: string, date?: string): EventSpaceList => {
  const now = new Date().toISOString()
  return {
    id: 0,
    created_at: now,
    updated_at: now,
    create_source: 'demo',
    update_author_id: null,
    slug: 'demo-event',
    name,
    location: location || 'New York, NY',
    latitude: null,
    longitude: null,
    seated_min_headcount: null,
    seated_max_headcount: null,
    standing_min_headcount: null,
    standing_max_headcount: null,
    space_memberships: [],
    event_space_list_dates: date
      ? [{ id: 1, start_date: date }]
      : [
          { id: 1, start_date: '2025-10-15' },
          { id: 2, start_date: '2025-10-16' },
        ],
  } as unknown as EventSpaceList
}

export const Route = createFileRoute('/present/cover/$template')({
  component: CoverRoute,
  validateSearch: (
    search: {
      name?: string
      location?: string
      date?: string
      venueName?: string
      venueAddress?: string
    } = {}
  ) => ({
    name: search.name,
    location: search.location,
    date: search.date,
    venueName: search.venueName,
    venueAddress: search.venueAddress,
  }),
})

function CoverRoute() {
  const { template } = Route.useParams() as { template: TemplateKey }
  const { name, location, date, venueName, venueAddress } = Route.useSearch()

  // Scale 1920x1080 canvas to fit viewport for crisp previews
  const BASE_WIDTH = 1920
  const BASE_HEIGHT = 1080
  const [scale, setScale] = useState(1)
  useEffect(() => {
    const computeScale = () => {
      const vw = window.innerWidth
      const vh = window.innerHeight
      setScale(Math.min(vw / BASE_WIDTH, vh / BASE_HEIGHT))
    }
    computeScale()
    window.addEventListener('resize', computeScale)
    return () => window.removeEventListener('resize', computeScale)
  }, [])

  // Use params to create event data, fallback to template-specific defaults
  const eventName = name || `${template === 'warburg' ? 'Warburg Pincus' : template === 'wp2' ? 'Warburg Pincus' : template === 'mt' ? 'M&T Bank' : template === 'freshfields' ? 'Freshfields' : 'Default'} Venue Options`
  const eventSpaceList = makeEventFromParams(eventName, location, date)

  if (template === 'warburg') {
    return (
      <div className="w-full h-screen flex items-center justify-center bg-black">
        <div style={{ width: BASE_WIDTH, height: BASE_HEIGHT, transform: `scale(${scale})`, transformOrigin: 'center center' }}>
          <WP2SlideCover eventSpaceList={eventSpaceList} />
        </div>
      </div>
    )
  }

  if (template === 'wp2') {
    return (
      <div className="w-full h-screen flex items-center justify-center bg-black">
        <div style={{ width: BASE_WIDTH, height: BASE_HEIGHT, transform: `scale(${scale})`, transformOrigin: 'center center' }}>
          <WP2SlideCover eventSpaceList={eventSpaceList} />
        </div>
      </div>
    )
  }

  if (template === 'mt') {
    return (
      <div className="w-full h-screen flex items-center justify-center bg-black">
        <div style={{ width: BASE_WIDTH, height: BASE_HEIGHT, transform: `scale(${scale})`, transformOrigin: 'center center' }}>
          <MTSlideCover eventSpaceList={eventSpaceList} />
        </div>
      </div>
    )
  }

  if (template === 'freshfields') {
    return (
      <div className="w-full h-screen flex items-center justify-center bg-black">
        <div style={{ width: BASE_WIDTH, height: BASE_HEIGHT, transform: `scale(${scale})`, transformOrigin: 'center center' }}>
          <FreshfieldsSlideCover eventSpaceList={eventSpaceList} />
        </div>
      </div>
    )
  }

  // Default template
  return (
    <div className="w-full h-screen flex items-center justify-center bg-black">
      <div style={{ width: BASE_WIDTH, height: BASE_HEIGHT, transform: `scale(${scale})`, transformOrigin: 'center center' }}>
        <NewDefaultSlideCover eventSpaceList={eventSpaceList} />
      </div>
    </div>
  )
}

export default CoverRoute
